import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import moment from 'moment'
import {
  faExclamationCircle,
  faQuestionCircle,
} from '@fortawesome/pro-regular-svg-icons'
import { times } from 'lodash'
import { useReselector } from '@/utils/sharedHooks'
import { getUserCreatedAt } from '@/selectors/user.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import { Accordion, Icon, Text, Select, Badge, CardColors } from '@/ui'
import { Tooltip } from './Tooltip'
import {
  selectEstimatesByQuarter,
  selectUserCreatedPastQteCutoff,
} from '../userTaxEstimates.selector'
import { EstimateStatus, statusForSingleEstimate } from '../service'
import { UserTaxEstimateSummary } from './UserTaxEstimateRow'
import { selectActiveQuarterlyTaxEstimateDetails } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'
import { fetchAllQuarterlyTaxEstimateDetailsIfNeeded } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'
import { TypographyStyles } from '@/styles'

const now = moment()
const currentYear = now.year()
const lastYear = now.subtract(1, 'year').year()

const yearOptions = [
  {
    text: currentYear.toString(),
    value: currentYear.toString(),
    key: currentYear.toString(),
  },
  {
    text: lastYear.toString(),
    value: lastYear.toString(),
    key: lastYear.toString(),
  },
]

const QuarterLabel = ({
  taxQuarter,
  taxYear,
}: {
  taxQuarter: number
  taxYear: number
}) => {
  const activeQuarterDetails = useReselector(
    selectActiveQuarterlyTaxEstimateDetails
  )
  const estimatesByQuarter = useReselector(selectEstimatesByQuarter, taxYear)
  const userJoinedTooLate = useReselector(selectUserCreatedPastQteCutoff)

  if (
    Number(activeQuarterDetails?.taxQuarter) === taxQuarter &&
    taxYear === Number(activeQuarterDetails?.taxYear)
  ) {
    return (
      <Badge className="float-right" color="green">
        Active Quarter
      </Badge>
    )
  }

  const key = `${taxYear}-${taxQuarter}`
  const estimates = estimatesByQuarter[key]

  return estimates?.some(
    (e) =>
      statusForSingleEstimate(e, userJoinedTooLate) ===
      EstimateStatus.unpaidWithoutResponse
  ) ? (
    <Tooltip
      popup={{
        body: 'We need this to calculate accurate Q4 estimates.',
      }}
      labelComponent={
        <Badge
          icon={faExclamationCircle}
          color="red"
          className="float-right -mt-[3px] mr-[18px]"
        >
          Please update your payment(s) for this quarter
        </Badge>
      }
    />
  ) : null
}
export const QuarterlyTaxPaymentHistory = () => {
  const dispatch = useAppDispatch()
  const { taxYear, taxQuarter } = useParams()
  const overrideTaxYear = !isNaN(Number(taxYear)) ? Number(taxYear) : null
  const overrideTaxQuarter = !isNaN(Number(taxQuarter))
    ? Number(taxQuarter)
    : null

  const activeQuarterDetails = useReselector(
    selectActiveQuarterlyTaxEstimateDetails
  )
  const userCreatedAt = useReselector(getUserCreatedAt)
  const userJoinedTooLate = useReselector(selectUserCreatedPastQteCutoff)

  const [dropdownValue, setDropdownValue] = useState(
    overrideTaxYear || Number(activeQuarterDetails?.taxYear)
  )

  const [accordionValue, setAccordionValue] = useState<string | null>(() => {
    if (overrideTaxQuarter) return `quarter-${overrideTaxQuarter}`
    if (overrideTaxYear && !overrideTaxQuarter) return null
    return activeQuarterDetails?.taxQuarter
      ? `quarter-${activeQuarterDetails.taxQuarter}`
      : null
  })

  const estimatesByQuarter = useReselector(
    selectEstimatesByQuarter,
    dropdownValue
  )

  useEffect(() => {
    dispatch(fetchAllQuarterlyTaxEstimateDetailsIfNeeded())
  }, [dispatch])

  if (!activeQuarterDetails) {
    return null
  }

  return (
    <div className="w-full space-y-6">
      {/* Top Row: Year Dropdown + Tooltip */}
      <div className="flex justify-start items-center gap-2">
        <div className="w-1/3">
          <Select
            value={dropdownValue.toString()}
            onValueChange={(value) => {
              const newYear = Number(value)
              setDropdownValue(newYear)
              setAccordionValue(null) // reset accordion when year changes
            }}
            options={yearOptions}
          />
        </div>
        <Tooltip
          className="w-fit"
          popup={{
            title: 'Quarterly Tax Payment History',
            body: 'Your personal record of past, present, and future quarterly tax payments. You can also update your payment status here.',
            link: 'https://support.joinheard.com/hc/en-us/articles/4416208037655-What-are-quarterly-tax-estimates-',
          }}
          labelComponent={
            <Icon icon={faQuestionCircle} className="text-neutral-500" />
          }
        />
      </div>

      {/* Accordion List */}
      <Accordion
        type="single"
        collapsible
        value={accordionValue || ''}
        onValueChange={setAccordionValue}
        variant="header"
      >
        {times(4).map((i) => {
          const taxQuarter = i + 1
          const key = `${dropdownValue}-${taxQuarter}`
          const estimates = estimatesByQuarter[key]
          const itemValue = `quarter-${taxQuarter}`

          return (
            <div
              key={key}
              className="border border-neutral-200 py-2 px-6 mb-3 rounded-2xl"
            >
              <Accordion.Item value={itemValue} className="border-none">
                <Accordion.Trigger className="w-full cursor-pointer flex items-center justify-between">
                  <>
                    <Text
                      variant={TypographyStyles.H3}
                      className="inline-block"
                    >
                      Q{taxQuarter} {dropdownValue}
                    </Text>
                    <div className="flex items-center gap-2">
                      <QuarterLabel
                        taxQuarter={taxQuarter}
                        taxYear={dropdownValue}
                      />
                    </div>
                  </>
                </Accordion.Trigger>

                <Accordion.Content>
                  {estimates?.length ? (
                    estimates.map((estimate) => (
                      <UserTaxEstimateSummary
                        key={`${estimate.id}-accordion-content`}
                        overrideBgColor={
                          statusForSingleEstimate(
                            estimate,
                            userJoinedTooLate
                          ) === EstimateStatus.unpaidWithoutResponse
                            ? CardColors.WARNING
                            : CardColors.LEVEL_1
                        }
                        estimate={estimate}
                      />
                    ))
                  ) : (
                    <Text variant={TypographyStyles.BODY_SM}>
                      Since you signed up in {moment.utc(userCreatedAt).year()},
                      Heard does not need your tax payments for this year.
                    </Text>
                  )}
                </Accordion.Content>
              </Accordion.Item>
            </div>
          )
        })}
      </Accordion>
    </div>
  )
}
